import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "src/components/ui/Card";
import { Button } from "src/components/ui/Button";
import { Calendar } from "src/components/ui/Calendar";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "src/components/ui/Select";
import { Textarea } from "src/components/ui/TextArea";
import { Label } from "src/components/ui/Label";
import { CheckCircle, Clock, AlertCircle, Loader2 } from "lucide-react";
import { ROUTES } from "src/routes/RouteConfig";
import { BookingStepProps } from "../types";
import { DURATION_OPTIONS } from "../constants";
import { getTutorName, getTutorSpecialties } from "../utils/tutorHelpers";
import { getDayOfWeek, getTutorAvailableDays, getAvailableTimeSlots } from "../utils/timeHelpers";
import { useTimezone, COMMON_TIMEZONES } from "src/hooks/useTimezone";
import TutorInfoCard from "./TutorInfoCard";
import ScrollToTop from "src/components/ScrollToTop";

const BookingStepOne: React.FC<BookingStepProps> = ({
  tutor,
  formData,
  onNext,
  onFormDataChange,
}) => {
  const navigate = useNavigate();
  const [availableTimeSlots, setAvailableTimeSlots] = useState<string[]>([]);
  const [timezoneConfirmed, setTimezoneConfirmed] = useState(false);
  const [showTimezoneSelector, setShowTimezoneSelector] = useState(false);
  const [loadingTimeSlots, setLoadingTimeSlots] = useState(false);
  
  // Use the existing timezone hook
  const {
    detectedTimezone,
    currentTimezone,
    getTimezoneDisplayInfo,
    formatTimeInTimezone,
  } = useTimezone();

  // Set default duration to 1 hour if not already set
  useEffect(() => {
    if (!formData.duration) {
      onFormDataChange({ duration: "60" });
    }
  }, [formData.duration, onFormDataChange]);

  // Update available time slots when date, timezone, or duration changes
  useEffect(() => {
    const loadTimeSlots = async () => {
      if (formData.date && tutor && formData.timezone && timezoneConfirmed) {
        setLoadingTimeSlots(true);
        try {
          const sessionDuration = parseInt(formData.duration || "60");
          const slots = await getAvailableTimeSlots(
            formData.date, 
            tutor, 
            formData.timezone,
            sessionDuration
          );
          setAvailableTimeSlots(slots);
        } catch (error) {
          console.error('Error loading time slots:', error);
          setAvailableTimeSlots([]);
        } finally {
          setLoadingTimeSlots(false);
        }
      } else {
        setAvailableTimeSlots([]);
      }
    };

    loadTimeSlots();
  }, [formData.date, formData.timezone, formData.duration, tutor, timezoneConfirmed]);

  const handleDateSelect = (date: Date | undefined) => {
    onFormDataChange({ 
      date, 
      timeSlot: "" // Reset time slot when date changes
    });
  };

  const handleTimeSlotSelect = (timeSlot: string) => {
    onFormDataChange({ timeSlot });
  };

  const handleDurationChange = (duration: string) => {
    onFormDataChange({ 
      duration,
      timeSlot: "" // Reset time slot when duration changes since slots will be different
    });
  };

  const handleTopicChange = (topic: string) => {
    onFormDataChange({ topic });
  };

  const handleNotesChange = (notes: string) => {
    onFormDataChange({ notes });
  };

  const handleTimezoneChange = (timezone: string) => {
    onFormDataChange({ timezone });
  };

  const isFormValid = formData.date && formData.timeSlot && formData.duration && formData.topic && formData.timezone;

  const availableDays = getTutorAvailableDays(tutor);

  return (
    <>
      <ScrollToTop />
      <div className="text-center mb-8">
        <h1 className="text-3xl font-extrabold text-gray-900 sm:text-4xl">
          Book a Session with {getTutorName(tutor)}
        </h1>
        <p className="mt-4 text-xl text-gray-500 max-w-2xl mx-auto">
          Select your preferred date and time for your tutoring session.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2">
          <Card>
            <CardHeader>
              <CardTitle>Select Date and Time</CardTitle>
              <CardDescription>
                Choose a date and time slot for your session with {getTutorName(tutor)}.
              </CardDescription>
            </CardHeader>
            <CardContent>
              {/* Timezone confirmation - show first */}
              {!timezoneConfirmed ? (
                <div className="mb-8">
                  <div className="p-6 bg-blue-50 border border-blue-200 rounded-lg">
                    <div className="flex items-start space-x-3">
                      <Clock className="h-6 w-6 text-blue-600 mt-0.5" />
                      <div className="flex-1 min-w-0">
                        <h3 className="text-lg font-medium text-blue-900 mb-3">
                          First, let's confirm your time zone
                        </h3>
                        
                        <div className="space-y-4">
                          <div className="text-sm text-blue-800">
                            We detected your time zone as:{" "}
                            <span className="font-semibold">
                              {getTimezoneDisplayInfo(detectedTimezone).label}
                            </span>
                          </div>
                          <div className="text-xs text-blue-600">
                            Current time: {formatTimeInTimezone(new Date(), detectedTimezone)}
                          </div>
                          
                          {showTimezoneSelector ? (
                            <div className="space-y-3">
                              <Label htmlFor="timezone-select" className="text-sm text-blue-800">
                                Select your time zone:
                              </Label>
                              <Select
                                value={formData.timezone || ""}
                                onValueChange={(value) => {
                                  handleTimezoneChange(value);
                                  setTimezoneConfirmed(true);
                                  setShowTimezoneSelector(false);
                                }}
                              >
                                <SelectTrigger>
                                  <SelectValue placeholder="Choose time zone..." />
                                </SelectTrigger>
                                <SelectContent>
                                  {COMMON_TIMEZONES.map((tz) => (
                                    <SelectItem key={tz.value} value={tz.value}>
                                      {tz.label}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => setShowTimezoneSelector(false)}
                                className="w-full"
                              >
                                Cancel
                              </Button>
                            </div>
                          ) : (
                            <div className="flex flex-col gap-2">
                              <Button
                                size="sm"
                                onClick={() => {
                                  setTimezoneConfirmed(true);
                                  handleTimezoneChange(detectedTimezone);
                                }}
                                className="bg-blue-600 hover:bg-blue-700 text-white w-full"
                              >
                                <CheckCircle className="h-4 w-4 mr-1" />
                                Yes, this is correct
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => setShowTimezoneSelector(true)}
                                className="border-blue-300 text-blue-700 hover:bg-blue-100 w-full"
                              >
                                <AlertCircle className="h-4 w-4 mr-1" />
                                No, let me choose
                              </Button>
                            </div>
                          )}
                          
                          <p className="text-xs text-blue-600 mt-3">
                            All times will be displayed in your confirmed time zone.
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <>
                  {/* Timezone confirmed - show compact confirmation */}
                  <div className="mb-6 p-3 bg-green-50 border border-green-200 rounded-lg">
                    <div className="flex items-center space-x-2 text-sm text-green-800">
                      <CheckCircle className="h-4 w-4 text-green-600" />
                      <span className="flex-1 min-w-0">
                        Time zone confirmed: {getTimezoneDisplayInfo(formData.timezone).label}
                      </span>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => {
                          setTimezoneConfirmed(false);
                          setShowTimezoneSelector(false);
                        }}
                        className="text-green-600 hover:text-green-800 p-1 h-auto flex-shrink-0"
                      >
                        Change
                      </Button>
                    </div>
                  </div>

                  {/* Date and Time Selection - only show after timezone is confirmed */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                    <div>
                      <h3 className="text-sm font-medium mb-2">Select a Date</h3>
                      <div className="border rounded-md">
                        <Calendar
                          mode="single"
                          selected={formData.date}
                          onSelect={handleDateSelect}
                          className="rounded-md"
                          disabled={(date) => {
                            // Disable dates in the past
                            const today = new Date();
                            today.setHours(0, 0, 0, 0);

                            const dayOfWeek = getDayOfWeek(date);
                            const isAvailable = availableDays.includes(dayOfWeek);
                            const isPast = date < today;
                            
                            return isPast || !isAvailable;
                          }}
                        />
                      </div>
                      <div className="mt-2 text-sm text-gray-500">
                        Available days: {availableDays.join(", ") || "Contact for availability"}
                      </div>
                    </div>

                    <div className="space-y-6">
                      {/* Session Duration */}
                      <div>
                        <h3 className="text-sm font-medium mb-2">Session Duration</h3>
                        <Select value={formData.duration || "60"} onValueChange={handleDurationChange}>
                          <SelectTrigger>
                            <SelectValue placeholder="Select duration" />
                          </SelectTrigger>
                          <SelectContent>
                            {DURATION_OPTIONS.map((option) => (
                              <SelectItem key={option.value} value={option.value}>
                                {option.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>

                      {/* Time Slot Selection */}
                      <div>
                        <h3 className="text-sm font-medium mb-2">Select a Time Slot</h3>
                        {formData.date ? (
                          loadingTimeSlots ? (
                            <div className="flex items-center justify-center p-8 text-gray-500">
                              <Loader2 className="h-6 w-6 animate-spin mr-2" />
                              Loading available time slots...
                            </div>
                          ) : availableTimeSlots.length > 0 ? (
                            <div className="grid grid-cols-2 gap-2">
                              {availableTimeSlots.map((time) => (
                                <Button
                                  key={time}
                                  variant={formData.timeSlot === time ? "default" : "outline"}
                                  className={formData.timeSlot === time ? "bg-rfpurple-600" : ""}
                                  onClick={() => handleTimeSlotSelect(time)}
                                >
                                  {time}
                                </Button>
                              ))}
                            </div>
                          ) : (
                            <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                              <p className="text-yellow-800 text-sm">
                                No available time slots for the selected date and duration.
                              </p>
                              <p className="text-yellow-600 text-xs mt-1">
                                Try selecting a different date or duration, or the tutor may have existing sessions scheduled.
                              </p>
                            </div>
                          )
                        ) : (
                          <p className="text-gray-500">Please select a date first.</p>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Session Topic and Notes - show after timezone confirmation */}
                  <div className="mt-8 space-y-4">
                    <div>
                      <h3 className="text-sm font-medium mb-2">Session Topic</h3>
                      <Select value={formData.topic} onValueChange={handleTopicChange}>
                        <SelectTrigger>
                          <SelectValue placeholder="Select a topic" />
                        </SelectTrigger>
                        <SelectContent>
                          {getTutorSpecialties(tutor).map((specialty: string) => (
                            <SelectItem key={specialty} value={specialty}>
                              {specialty}
                            </SelectItem>
                          ))}
                          <SelectItem value="custom">
                            Other (specify in notes)
                          </SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <h3 className="text-sm font-medium mb-2">Additional Notes</h3>
                      <Textarea
                        placeholder="Share any specific questions or topics you'd like to cover"
                        value={formData.notes}
                        onChange={(e) => handleNotesChange(e.target.value)}
                        className="min-h-[100px]"
                      />
                    </div>
                  </div>
                </>
              )}
            </CardContent>
            <CardFooter className="flex justify-between">
              <Button
                variant="outline"
                onClick={() => navigate(ROUTES.BOOK_DEMO.path, { state: { activeTab: 'search' } })}
              >
                Back to Tutors
              </Button>
              <Button
                className="button-gradient text-white"
                disabled={!isFormValid}
                onClick={onNext}
              >
                Continue to Review
              </Button>
            </CardFooter>
          </Card>
        </div>

        <div>
          <TutorInfoCard tutor={tutor} />
        </div>
      </div>
    </>
  );
};

export default BookingStepOne;